<template>
  <div v-if="groups.length || hasUngrouped" ref="rootRef" class="group-filter" @keydown.esc="onEscape">
    <span v-if="label" class="group-filter-label">{{ label }}</span>
    <button
      ref="triggerRef"
      type="button"
      data-testid="group-filter-trigger"
      class="group-filter-trigger"
      :class="{ open }"
      :aria-expanded="open"
      :aria-label="triggerAriaLabel"
      @click="toggleOpen"
    >
      <svg class="trigger-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 5h18M6 12h12M10 19h4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
      <span data-testid="group-filter-summary" class="group-filter-summary">{{ summary }}</span>
      <svg class="trigger-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 10l5 5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
    <div v-show="open" class="group-filter-panel" @keydown="onPanelKeydown">
      <input
        v-model="search"
        type="text"
        data-testid="group-filter-search"
        class="group-filter-search"
        placeholder="Search..."
        aria-label="Search groups"
      />
      <div class="group-filter-actions">
        <button
          type="button"
          data-testid="group-filter-select-all"
          class="group-filter-action"
          @click="onSelectAll"
        >
          Select all
        </button>
        <button
          type="button"
          data-testid="group-filter-deselect-all"
          class="group-filter-action"
          @click="onDeselectAll"
        >
          Deselect all
        </button>
      </div>
      <ul class="group-filter-list" role="listbox" aria-multiselectable="true">
        <li v-for="group in visibleGroups" :key="group.id" class="group-filter-item">
          <label class="group-filter-option">
            <input
              type="checkbox"
              :data-testid="`group-checkbox-${group.id}`"
              :aria-label="optionAriaLabel(group.name, !excluded.has(group.id))"
              :checked="!excluded.has(group.id)"
              @change="onToggle(group.id, $event.target.checked)"
            />
            <span class="group-filter-name">{{ group.name }}</span>
          </label>
        </li>
      </ul>
      <ul
        v-if="hasUngrouped"
        class="group-filter-list group-filter-ungrouped"
        role="listbox"
        aria-multiselectable="true"
      >
        <li class="group-filter-item">
          <label class="group-filter-option">
            <input
              type="checkbox"
              data-testid="group-checkbox-ungrouped"
              :aria-label="optionAriaLabel('Ungrouped', !excluded.has(null))"
              :checked="!excluded.has(null)"
              @change="onToggle(null, $event.target.checked)"
            />
            <span class="group-filter-name">Ungrouped</span>
          </label>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue'

const props = defineProps({
  groups: {
    type: Array,
    required: true
  },
  excluded: {
    type: Set,
    required: true
  },
  label: {
    type: String,
    default: ''
  },
  hasUngrouped: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:excluded'])

const search = ref('')
const open = ref(false)
const triggerRef = ref(null)
const rootRef = ref(null)

const onDocumentClick = (e) => {
  if (rootRef.value && !rootRef.value.contains(e.target)) {
    open.value = false
  }
}

watch(open, (isOpen) => {
  if (isOpen) {
    document.addEventListener('click', onDocumentClick, true)
  } else {
    document.removeEventListener('click', onDocumentClick, true)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick, true)
})

const toggleOpen = () => {
  open.value = !open.value
}

const onEscape = () => {
  if (!open.value) return
  open.value = false
  triggerRef.value?.focus()
}

const onPanelKeydown = (e) => {
  const isArrow = e.key === 'ArrowDown' || e.key === 'ArrowUp'
  const isSpace = e.key === ' ' || e.key === 'Spacebar'
  if (!isArrow && !isSpace) return

  const boxes = Array.from(e.currentTarget.querySelectorAll('input[type="checkbox"]'))
  if (boxes.length === 0) return
  const current = boxes.indexOf(document.activeElement)

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    boxes[current < 0 ? 0 : Math.min(current + 1, boxes.length - 1)].focus()
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    boxes[current < 0 ? 0 : Math.max(current - 1, 0)].focus()
  } else if (current >= 0) {
    e.preventDefault()
    boxes[current].click()
  }
}

const visibleGroups = computed(() => {
  const term = search.value.trim().toLowerCase()
  if (!term) return props.groups
  return props.groups.filter((g) => g.name.toLowerCase().includes(term))
})

const summary = computed(() => {
  if (props.excluded.size === 0) return 'All groups'
  const names = props.groups.filter((g) => !props.excluded.has(g.id)).map((g) => g.name)
  if (names.length === 0) return 'No groups'
  if (names.length === 1) return names[0]
  if (names.length <= 3) return names.join(', ')
  return `${names[0]}, ${names[1]} +${names.length - 2}`
})

const triggerAriaLabel = computed(() => {
  if (props.excluded.size === 0) return 'Group filter, all groups selected'
  const selected = props.groups.filter((g) => !props.excluded.has(g.id)).length
  return `Group filter, ${selected} of ${props.groups.length} groups selected`
})

const optionAriaLabel = (name, visible) => `Group ${name}, ${visible ? 'shown' : 'hidden'}`

const onSelectAll = () => {
  emit('update:excluded', new Set())
}

const onDeselectAll = () => {
  const ids = props.groups.map((g) => g.id)
  if (props.hasUngrouped) ids.push(null)
  emit('update:excluded', new Set(ids))
}

const onToggle = (id, checked) => {
  const next = new Set(props.excluded)
  if (checked) {
    next.delete(id)
  } else {
    next.add(id)
  }
  emit('update:excluded', next)
}
</script>

<style scoped>
.group-filter {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
}

.group-filter-trigger {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  color: var(--color-text);
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 200px;
}

.group-filter-trigger:hover,
.group-filter-trigger.open {
  color: var(--highlights);
  border-color: rgba(123, 24, 28, 0.2);
  box-shadow: 0 4px 12px rgba(123, 24, 28, 0.12);
}

.group-filter-trigger:active {
  transform: scale(0.98);
}

.trigger-icon {
  flex-shrink: 0;
  color: var(--highlights);
  opacity: 0.75;
  transition: opacity 0.2s ease;
}

.group-filter-trigger:hover .trigger-icon,
.group-filter-trigger.open .trigger-icon {
  opacity: 1;
}

.group-filter-summary {
  flex: 1;
  text-align: left;
  white-space: nowrap;
}

.trigger-chevron {
  flex-shrink: 0;
  opacity: 0.5;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.group-filter-trigger:hover .trigger-chevron,
.group-filter-trigger.open .trigger-chevron {
  opacity: 1;
}

.group-filter-trigger.open .trigger-chevron {
  transform: rotate(180deg);
}

.group-filter-panel {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 10;
  margin-top: 6px;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: var(--color-background, #fff);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-card);
  box-shadow: var(--shadow-card-hover);
}

.group-filter-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text);
  opacity: 0.55;
}

.group-filter-search {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
  color: var(--color-text);
  background: var(--color-background, #fff);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.group-filter-search:focus {
  outline: none;
  border-color: rgba(123, 24, 28, 0.4);
  box-shadow: 0 0 0 2px rgba(123, 24, 28, 0.1);
}

.group-filter-actions {
  display: flex;
  gap: 8px;
}

.group-filter-action {
  flex: 1;
  padding: 6px 8px;
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  color: var(--highlights);
  background: var(--highlights-background);
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.group-filter-action:hover {
  background: var(--highlights-hover);
  border-color: rgba(123, 24, 28, 0.2);
}

.group-filter-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 240px;
  overflow-y: auto;
}

.group-filter-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.12s ease;
}

.group-filter-option:hover {
  background: var(--highlights-background);
}

.group-filter-option input[type='checkbox'] {
  accent-color: var(--highlights);
  width: 15px;
  height: 15px;
  cursor: pointer;
}

.group-filter-ungrouped {
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid var(--color-border);
}
</style>
